import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Form } from '@unform/web';
import {
  subDays,
  addDays,
  format,
  getDate,
  isAfter,
  parseISO,
  formatDistance,
} from 'date-fns';
import pt from 'date-fns/locale/pt';
import { uniqueId } from 'lodash';
import { filesize } from 'filesize';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

import { updateCurrentDate } from 'store/modules/usuario/actions';
import { show } from 'store/modules/dialog/actions';
import api from 'services/api';
import midiainfo from 'utils/midiainfo';

import {
  FiSearch,
  FiTrash2,
  FiInfo,
  FiTv,
  FiActivity,
  FiFileText,
} from 'react-icons/fi';

import Switch from 'components/Switch/withoutForm';
import SelectDate from 'components/SelectDate';
import Button from 'components/Button';
import Input from 'components/Input';
import Pagination from 'components/Pagination';
import LoadingTable from 'components/LoadingTable';
import Flag from 'components/Flag';
import Card from 'components/Card';
import Upload from 'components/Upload';
import Tooltip from 'components/Tooltip';
import DropDownMenu from 'components/DropDownMenu';
import PlayerBaixa from 'components/PlayerBaixa';
import NotFound from 'components/NotFound';

import SpreadSheets from 'assets/spreadsheets.svg';

import { Container, Table, InfoPlanilha, TdFlag, Td } from './styles';
import { Scroll } from 'styles/global';

import {
  Emissora,
  Comercial as IComercial,
  EnviaExibidor as IEnviaExibidor,
} from 'pages/@types';
import AppError from 'utils/AppError';
import { StatusEnviaExibidor } from 'utils/variables';

interface EnviaExibidor extends IEnviaExibidor {
  emissoraorigem: Emissora;
  loading: boolean;
}

export default function Comercial() {
  // react-router-dom
  const navigate = useNavigate();

  const { emissora, currentDate } = useSelector((state: any) => state.usuario);
  const dispatch = useDispatch();
  const { callback: callbackDialog } = useSelector(
    (state: any) => state.dialog,
  );

  const [loading, setLoading] = useState(true);
  const [comerciaisCompleto, setComerciaisCompleto] = useState<EnviaExibidor[]>(
    [],
  );
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [filtro, setFiltro] = useState('');
  const [ultimaPlanilha, setUltimaPlanilha] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]) as any;
  const [selected, setSelected] = useState<EnviaExibidor | null>(null);
  const [player, setPlayer] = useState({
    open: false,
    midia_url: '',
    pipe: false,
  });

  const comerciais = comerciaisCompleto.filter(
    c =>
      c.comercial.titulo.includes(filtro.toUpperCase()) ||
      c.comercial.codmaterial.toString().includes(filtro) ||
      c.comercial.cliente.mnemonico.includes(filtro.toUpperCase()),
  );

  const dateFormatted = useMemo(() => {
    if (currentDate) {
      return format(new Date(currentDate), "d 'de' MMMM", { locale: pt });
    } else return format(new Date(), "d 'de' MMMM", { locale: pt });
  }, [currentDate]);

  const handleSwitchChange = useCallback(async (e, comercial) => {
    setComerciaisCompleto(prevState =>
      prevState.map(c => (c.id === comercial.id ? { ...c, loading: true } : c)),
    );

    try {
      const { data } = await api.put(
        `/globoexpress/${comercial.comercial.id}`,
        { globoexpress: e.target.checked },
      );
      setComerciaisCompleto(prevState =>
        prevState.map(c =>
          c.id === comercial.id
            ? {
                ...c,
                loading: false,
                comercial: {
                  ...c.comercial,
                  globoexpress: data.globoexpress,
                },
              }
            : c,
        ),
      );

      toast.success('Prioridade alterada');
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        const appError = error as AppError;
        toast.error(appError.message);
      }
    }
  }, []);

  const handlePrevDay = useCallback(() => {
    setPage(1);
    dispatch(updateCurrentDate(subDays(new Date(currentDate), 1)));
  }, [currentDate, dispatch]);

  const handleNextvDay = useCallback(() => {
    setPage(1);
    dispatch(updateCurrentDate(addDays(new Date(currentDate), 1)));
  }, [currentDate, dispatch]);

  const handleDateChange = useCallback(
    date => {
      date && dispatch(updateCurrentDate(date));
    },
    [dispatch],
  );

  const handleMidiaInfoDialog = useCallback(
    async id => {
      const response = await api.get(`/midias/${id}`);
      if (response && response.data && response.data.pendente) {
        const comercial = response.data.pendente;
        if (comercial && comercial.midia && comercial.midia.midiainfo) {
          if (
            comercial.midia.midiainfo8ch &&
            comercial.midia.statuspreavaliacao8ch
          ) {
            const options = midiainfo(
              comercial.midia.statuspreavaliacao8ch,
              comercial.midia.midiainfo8ch,
            );
            dispatch(show(options));
          }
        }
      } else toast.error('Falha de comunicação com o sistema');
    },
    [dispatch],
  );

  const handleUpload = useCallback(
    files => {
      const filesFormatted = files.map((file: any) => ({
        file,
        id: uniqueId(),
        name: file.name,
        readableSize: filesize(file.size),
        progress: 0,
        uploaded: false,
        error: false,
      }));

      setSelectedFiles(filesFormatted);
    },
    [setSelectedFiles],
  );

  function handleDelete(enviaExibidor: EnviaExibidor) {
    setSelected(enviaExibidor);
    const optionBase = {
      type: 'question',
      text: `Arquivar ${enviaExibidor.comercial.titulo} ?`,
      confirmButtonText: 'CONFIRMAR',
      callback: {
        url: `/enviaexibidor/${enviaExibidor.id}`,
        method: api.delete,
      },
      haveCallback: true,
      successMessage: `Envio de ${enviaExibidor.comercial.titulo} para ${emissora.value} apagado`,
      cancelButtonText: 'NÃO',
      showCancelButton: false,
      showConfirmButton: true,
      showCloseButton: true,
    };
    dispatch(show(optionBase));
  }
  function handlePlayer(comercial: IComercial) {
    if (!!comercial.midia && comercial.midia.preview_url) {
      setPlayer({
        open: !player.open,
        midia_url: comercial.midia.preview_url,
        pipe: false,
      });
    } else {
      dispatch(
        show({
          type: 'warning',
          text: 'Preview indisponível no momento',
          showConfirmButton: false,
        }),
      );
    }
  }

  function handleActionSelect(c: EnviaExibidor, action: { value: number }) {
    switch (action.value) {
      case 1:
        handlePlayer(c.comercial);
        break;
      case 2:
        handleDelete(c);
        break;
      case 3:
        if (c.comercial.midia) handleMidiaInfoDialog(c.comercial.midia.id);
        else toast.error('Falha ao verificar informações da mídia');
        break;
      default:
        break;
    }
  }

  // fecha player
  function closeVideo() {
    setPlayer({
      open: !player.open,
      midia_url: '',
      pipe: false,
    });
  }

  const updateFile = useCallback(
    (id, data) => {
      setSelectedFiles((prevState: any) =>
        prevState.map((uploadedFile: any) =>
          id === uploadedFile.id ? { ...uploadedFile, ...data } : uploadedFile,
        ),
      );
    },
    [setSelectedFiles],
  );

  const handleSuccessMessage = useCallback(() => {
    const option = {
      type: 'success',
      confirmButtonText: '',
      contentHTML: 'Planilha adicionada',
      timer: 3000,
      cancelButtonText: 'NÃO',
      showCancelButton: false,
      showConfirmButton: false,
      showCloseButton: true,
    };
    dispatch(show(option));
  }, [dispatch]);

  const handleErrorMessage = useCallback(
    message => {
      const option = {
        type: 'error',
        confirmButtonText: '',
        text: message,
        timer: 3000,
        cancelButtonText: 'NÃO',
        showCancelButton: false,
        showConfirmButton: false,
        showCloseButton: true,
      };
      dispatch(show(option));
    },
    [dispatch],
  );

  const processUpload = useCallback(
    async selectedFile => {
      try {
        const data = new FormData();

        data.append('file', selectedFile.file);
        await api.post(`/emissoras/${emissora.value}/planilha`, data, {
          onUploadProgress: e => {
            const progress = e.total
              ? Math.round((e.loaded * 100) / e.total)
              : 0;
            updateFile(selectedFile.id, {
              progress,
            });
          },
        });

        updateFile(selectedFile.id, {
          uploaded: true,
        });
        setTimeout(() => setSelectedFiles([]), 2000);
        handleSuccessMessage();
        setLoading(true);
        const responseComerciais = await api.get(`enviaexibidor/diario`, {
          params: {
            emissora_id: emissora.value,
            date: format(new Date(currentDate), 'yyyy-MM-dd'),
            // page,
            // search: filtro,
          },
        });

        if (responseComerciais && responseComerciais.data) {
          setComerciaisCompleto(
            responseComerciais.data.comerciais.docs.map((c: EnviaExibidor) => ({
              ...c,
              loading: false,
            })),
          );
          setPages(responseComerciais.data.comerciais.pages);
          if (responseComerciais.data.ultimaplanilha)
            setUltimaPlanilha(
              responseComerciais.data.ultimaplanilha.created_at,
            );
        } else toast.error('Falha de comunicação com o sistema');
        setLoading(false);
      } catch (err) {
        updateFile(selectedFile.id, {
          error: true,
        });
        setLoading(false);
        setUltimaPlanilha(null);
        setTimeout(() => setSelectedFiles([]), 2000);
        if (err instanceof Error) {
          handleErrorMessage(err.message);
        } else {
          const appError = err as AppError;
          handleErrorMessage(appError.message);
        }
      }
    },
    [
      emissora.value,
      updateFile,
      handleSuccessMessage,
      currentDate,
      // filtro,
      setSelectedFiles,
      handleErrorMessage,
    ],
  );

  useEffect(
    () =>
      selectedFiles.forEach((selectedFile: any) => {
        if (
          !selectedFile.uploaded &&
          !selectedFile.error &&
          selectedFile.progress === 0
        ) {
          processUpload(selectedFile);
        }
      }),
    [processUpload, selectedFiles],
  );

  useEffect(() => {
    const controller = new AbortController();
    async function loadComerciais() {
      try {
        const response = await api.get(`enviaexibidor/diario`, {
          signal: controller.signal,
          params: {
            emissora_id: emissora.value,
            date: format(new Date(currentDate), 'yyyy-MM-dd'),
            // page,
            // search: filtro,
          },
        });

        if (response && response.data) {
          setComerciaisCompleto(
            response.data.comerciais.docs.map((c: EnviaExibidor) => ({
              ...c,
              loading: false,
            })),
          );
          setPages(response.data.comerciais.pages);
          if (response.data.ultimaplanilha)
            setUltimaPlanilha(response.data.ultimaplanilha.created_at);
        } else toast.error('Falha de comunicação com o sistema');
      } catch (error) {
        if (error instanceof Error) {
          toast.error(error.message);
        } else {
          const appError = error as AppError;
          toast.error(appError.message);
        }
      }
      setLoading(false);
    }
    setLoading(true);
    loadComerciais();
    return () => {
      controller.abort();
    };
  }, [currentDate, emissora.value]);

  useEffect(() => {
    if (callbackDialog && callbackDialog.success)
      if (callbackDialog.success.action === 'success' && selected) {
        setComerciaisCompleto(prevState =>
          prevState.map(c =>
            c.id === selected.id
              ? {
                  ...c,
                  deleted_at: new Date().toString(),
                  statusgeral: {
                    order: 14,
                    nome: 'ARQUIVADO',
                    type: 'arquivado',
                    info: 'Envio arquivado',
                  },
                }
              : c,
          ),
        );
      }
  }, [callbackDialog, selected]);

  return (
    <Container>
      <header>
        <Upload
          accept={{
            'application/vnd.ms-excel': ['.xls'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
              '.xlsx',
            ],
          }}
          onUpload={handleUpload}
          selectedFiles={selectedFiles}
          disabled={isAfter(
            getDate(new Date()),
            getDate(new Date(currentDate)),
          )}
        />

        <SelectDate
          dateFormatted={dateFormatted}
          currentDateZoned={format(
            addDays(new Date(currentDate), 1),
            'yyyy-MM-dd',
          )}
          handleNextDay={handleNextvDay}
          handleDateChange={handleDateChange}
          handlePrevDay={handlePrevDay}
          disabledNextDay={
            getDate(new Date(currentDate)) === getDate(new Date())
          }
        />
        <InfoPlanilha>
          <p>
            {ultimaPlanilha && (
              <>
                Planilha carregada{' '}
                {formatDistance(parseISO(ultimaPlanilha), new Date(), {
                  addSuffix: true,
                  locale: pt,
                })}
              </>
            )}
          </p>
        </InfoPlanilha>
      </header>

      <Card height="calc(100vh - 88px)">
        <>
          <header>
            <h4>LISTA DE MATERIAIS</h4>
            {/* <Pagination
              total={pages}
              activePage={page}
              onClick={page => setPage(page)}
            /> */}
            <div>
              <Form onSubmit={() => {}}>
                <Input
                  name="searchLocal"
                  placeholder="Buscar na Tabela"
                  icon={<FiSearch />}
                  onChange={e => setFiltro(e.target.value)}
                />
              </Form>
            </div>
          </header>
          {comerciais.length > 0 && !loading && (
            <Scroll>
              <Table>
                <thead>
                  <tr>
                    <th />
                    <th style={{ width: '4rem' }}>CM</th>
                    <th>TÍTULO</th>
                    <th style={{ width: '10rem', textAlign: 'center' }}>
                      STATUS
                    </th>
                    <th style={{ width: '7rem', textAlign: 'center' }}>
                      PTOVENDA
                    </th>
                    <th style={{ width: '4rem', textAlign: 'center' }}>DUR.</th>
                    <th style={{ width: '8rem', textAlign: 'center' }}>
                      PRIORITÁRIO
                    </th>
                    <th style={{ width: '4rem' }}>ORIGEM</th>
                    <th style={{ width: '1rem' }} />
                  </tr>
                </thead>

                <tbody>
                  {!!comerciais &&
                    comerciais.map((c, index) => (
                      <tr key={c.id}>
                        <TdFlag $isGe={c.comercial.globoexpress}>
                          <div id={`tdFlag_id${index}`} />
                          <Tooltip
                            target={`tdFlag_id${index}`}
                            position="right"
                          >
                            <p>
                              <strong>PRIORITÁRIO</strong>
                            </p>
                          </Tooltip>
                        </TdFlag>
                        <Td disabled={c.deleted_at !== null}>
                          {c.comercial.codmaterial}
                        </Td>
                        <Td disabled={c.deleted_at !== null}>
                          {c.comercial.titulo}{' '}
                          <strong>({c.comercial.cliente.mnemonico})</strong>
                        </Td>
                        <Td>
                          <Flag
                            disabled={c.deleted_at !== null}
                            name={c.statusgeral.nome}
                            type={c.statusgeral.type}
                            tooltip={c.statusgeral.info}
                          />
                        </Td>
                        <Td
                          style={{ textAlign: 'center' }}
                          disabled={c.deleted_at !== null}
                        >
                          {c.comercial.ptovenda}
                        </Td>
                        <Td
                          style={{ textAlign: 'center' }}
                          disabled={c.deleted_at !== null}
                        >
                          {c.comercial.duracao} s
                        </Td>
                        <Td>
                          <Switch
                            disabled={c.deleted_at !== null}
                            id={c.id}
                            loading={c.loading}
                            onChange={e => handleSwitchChange(e, c)}
                            checked={c.comercial.globoexpress}
                          />
                        </Td>
                        <td>{c.emissoraorigem?.sigla}</td>
                        <Td style={{ display: 'flex' }}>
                          <Button
                            disabled={c.deleted_at !== null}
                            fill
                            border
                            color={c.planilhagravacao ? 'success' : 'default'}
                            round
                            id={`id_planilha_${index}`}
                          >
                            <FiFileText />
                          </Button>
                          <Tooltip target={`id_planilha_${index}`}>
                            {c.planilhagravacao
                              ? 'COM PLANILHA'
                              : 'SEM PLANILHA'}
                          </Tooltip>

                          <DropDownMenu
                            disabled={false}
                            id={`acoes${index}`}
                            actions={[
                              {
                                value: 0,
                                label: 'HISTÓRICO',
                                icon: <FiActivity />,
                                href: `/historico/${c.comercial.id}`,
                              },
                              {
                                value: 1,
                                label: 'ASSISTIR',
                                icon: <FiTv />,
                                disabled:
                                  c.deleted_at !== null ||
                                  !c.comercial ||
                                  !c.comercial.midia ||
                                  !c.comercial.midia.preview_url,
                              },
                              {
                                value: 2,
                                label: 'ARQUIVAR',
                                icon: <FiTrash2 />,
                                disabled:
                                  c.deleted_at !== null ||
                                  c.status === StatusEnviaExibidor.CONCLUIDO ||
                                  c.status === StatusEnviaExibidor.INGESTMANUAL,
                              },
                              {
                                value: 3,
                                label: 'MIDIAINFO',
                                icon: <FiInfo />,
                                disabled:
                                  c.deleted_at !== null ||
                                  !c.comercial ||
                                  !c.comercial.midia ||
                                  c.comercial.midia.statuspreavaliacao ===
                                    'pendente' ||
                                  c.comercial.midia.statuspreavaliacao ===
                                    'NA' ||
                                  c.comercial.midia.statuspreavaliacao ===
                                    'erro' ||
                                  c.comercial.midia.statuspreavaliacao ===
                                    'processando',
                              },
                            ]}
                            handleClick={action =>
                              !action.disabled && handleActionSelect(c, action)
                            }
                          />
                        </Td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            </Scroll>
          )}
        </>

        {comerciais.length === 0 && !loading && (
          <div>
            <NotFound img={SpreadSheets} title="PLANILHAS DE GRAVAÇÃO">
              Nenhuma planilha carregada
            </NotFound>
          </div>
        )}
        {loading && <LoadingTable type="materiais" />}
      </Card>
      <PlayerBaixa
        open={player.open}
        toggleModal={() => closeVideo()}
        pipe={player.pipe}
        url={player.midia_url}
      />
    </Container>
  );
}
